import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Modal } from "antd";
import { Context, Hooks, Util } from "@xt/xtbase";
import modalStyles from "@/components/Pages/SpikexCard/spikexModal.module.less";
import {
  isUserMobileBound,
  markBindPhonePromptShown,
  shouldDeferBindPhoneForHomeCouponPrompt,
  shouldShowBindPhonePrompt,
  shouldSkipBindPhonePromptInApp,
  shouldSkipBindPhonePromptPath,
} from "@/utils/bindPhonePrompt";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

const PROMPT_DELAY_MS = 1500;

function isMockBindPhonePromptPreview(query) {
  const v = query?.mockBindPhonePrompt;
  if (v == null) return false;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

export default function BindPhonePrompt() {
  const router = useRouter();
  const { t } = useTranslation();
  const [appState] = useContext(XtContext);
  const { loginStatus, userInfo } = appState;
  const userId = userInfo?.userId;

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const isMockPreview = useMemo(
    () => isMockBindPhonePromptPreview(router.query),
    [router.query?.mockBindPhonePrompt]
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return undefined;

    let cancelled = false;
    let timerId = null;

    const openPrompt = () => {
      if (cancelled) return;
      markBindPhonePromptShown(userId);
      setVisible(true);
    };

    const scheduleOpen = () => {
      timerId = window.setTimeout(openPrompt, PROMPT_DELAY_MS);
    };

    const run = () => {
      if (isMockPreview) {
        if (loginStatus === 1) {
          setVisible(true);
        }
        return;
      }

      if (loginStatus !== 1 || !userId || !userInfo) return;
      if (shouldSkipBindPhonePromptInApp()) return;
      if (userInfo.userAccountLevel === 2) return;
      if (isUserMobileBound(userInfo)) return;
      if (shouldSkipBindPhonePromptPath(router.pathname)) return;
      if (shouldDeferBindPhoneForHomeCouponPrompt(userId, userInfo, router.pathname)) return;
      if (!shouldShowBindPhonePrompt(userId)) return;

      scheduleOpen();
    };

    run();

    return () => {
      cancelled = true;
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [
    mounted,
    isMockPreview,
    loginStatus,
    userId,
    userInfo,
    router.pathname,
  ]);

  const closePrompt = useCallback(() => {
    setVisible(false);
  }, []);

  const handleDismiss = useCallback(() => {
    if (!isMockPreview && userId) {
      markBindPhonePromptShown(userId);
    }
    closePrompt();
  }, [closePrompt, isMockPreview, userId]);

  const handleLink = useCallback(() => {
    if (!isMockPreview && userId) {
      markBindPhonePromptShown(userId);
    }
    closePrompt();
    window.location.href = getUrl("/accounts/user/mobile");
  }, [closePrompt, isMockPreview, userId]);

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable
      onCancel={handleDismiss}
      centered
      width={360}
      destroyOnClose
      className={modalStyles.modalRoot}
      wrapClassName={modalStyles.modalWrap}
      zIndex={10050}
    >
      <div className={modalStyles.modalBody}>
        <div className={modalStyles.modalTitle}>{t("account.bindPhonePromptTitle")}</div>
        <div className={modalStyles.modalDesc}>{t("account.bindPhonePromptBody")}</div>
        <div className={`${modalStyles.modalActions} ${modalStyles.modalActionsSingle}`}>
          <button type="button" className={modalStyles.btnPrimary} onClick={handleLink}>
            {t("account.bindPhonePromptLink")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
